import React, { useMemo } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertTriangle, Zap } from 'lucide-react';

const CapabilityLedger = ({ machine, capabilities }) => {
  const requiredCapabilities = useMemo(() => {
    if (!machine?.effects) return [];
    
    const caps = new Set();
    Object.values(machine.effects).forEach((effect) => {
      effect.required_capabilities?.forEach((cap) => {
        caps.add(cap.name);
      });
    });
    
    return Array.from(caps);
  }, [machine]);

  const effectsByCapability = useMemo(() => {
    if (!machine?.effects) return {};
    
    const grouped = {};
    Object.entries(machine.effects).forEach(([name, effect]) => {
      effect.required_capabilities?.forEach((cap) => {
        if (!grouped[cap.name]) grouped[cap.name] = [];
        grouped[cap.name].push(name);
      });
    });
    
    return grouped;
  }, [machine]);

  return (
    <ScrollArea className="h-full" data-testid="capability-ledger">
      <div className="p-3 space-y-4">
        <div>
          <h3 className="text-xs font-mono text-gray-500 mb-3 uppercase tracking-wider flex items-center">
            <Zap className="w-3 h-3 mr-1.5 text-yellow-500" />
            Required Capabilities
          </h3>
          
          {requiredCapabilities.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-500">
              No capabilities required
            </div>
          ) : (
            <div className="space-y-2">
              {requiredCapabilities.map((cap) => {
                const isAvailable = capabilities?.capabilities?.includes(cap);
                
                return (
                  <div
                    key={cap}
                    className={`p-2.5 rounded border ${
                      isAvailable
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-orange-500/10 border-orange-500/30'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-1.5">
                        {isAvailable ? (
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                        )}
                        <span className="font-mono text-xs font-medium">{cap}</span>
                      </div>
                      <Badge
                        variant={isAvailable ? 'default' : 'destructive'}
                        className="text-[10px] h-4 px-1.5"
                      >
                        {isAvailable ? 'OK' : 'REQ'}
                      </Badge>
                    </div>
                    
                    {effectsByCapability[cap] && (
                      <div className="pt-2 border-t border-white/10">
                        <div className="text-[10px] text-gray-500 mb-1 font-mono">
                          Effects:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {effectsByCapability[cap].map((effectName) => (
                            <Badge key={effectName} variant="outline" className="text-[10px] h-4 px-1.5 font-mono">
                              {effectName}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {machine?.effects && Object.keys(machine.effects).length > 0 && (
          <div>
            <h4 className="text-xs font-mono text-gray-500 mb-2 uppercase tracking-wider">
              Effect Registry
            </h4>
            <div className="space-y-1.5">
              {Object.entries(machine.effects).map(([name, effect]) => (
                <div
                  key={name}
                  className="p-2 bg-white/5 border border-white/10 rounded"
                >
                  <div className="font-mono text-xs font-medium mb-1.5">{name}</div>
                  <div className="space-y-0.5 text-[10px] text-gray-500 font-mono">
                    <div>in: {effect.input_type}</div>
                    <div>out: {effect.output_type}</div>
                    <div>err: {effect.failure_type}</div>
                    {effect.required_capabilities?.length > 0 && (
                      <div className="mt-1.5 pt-1.5 border-t border-white/10">
                        <div className="flex flex-wrap gap-0.5">
                          {effect.required_capabilities.map((cap, idx) => (
                            <Badge key={idx} variant="outline" className="text-[9px] h-4 px-1">
                              {cap.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default CapabilityLedger;
